import { t as tr } from '../i18n.js'
import { Section, jumpToSection } from '../section.js'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { startRegistration } from '@simplewebauthn/browser'
import QRCode from 'qrcode'
import { CopyButton } from '../copy.js'
import { api, ApiError, type ApiToken, type MfaStatus } from '../api.js'
import { useAuth } from '../auth.js'
import { fmt } from './Account.js'

/** Account → Security: authenticator app, recovery codes, passkeys, network restriction. */
export function SecurityPanel() {
  const { user, refresh } = useAuth()
  const qc = useQueryClient()
  const status = useQuery({ queryKey: ['mfa'], queryFn: () => api<MfaStatus>('/auth/mfa/status') })
  const reload = async () => { await qc.invalidateQueries({ queryKey: ['mfa'] }); await refresh() }
  if (!status.data) return null
  const s = status.data
  return (
    <>
      {s.required && (
        <p className="notice bad-border">{tr("Your role requires a second factor for password sign-in. Set up an")} <a href="#acct-totp" onClick={(e) => { e.preventDefault(); jumpToSection('acct-totp') }}>authenticator app</a> or add a <a href="#acct-passkeys" onClick={(e) => { e.preventDefault(); jumpToSection('acct-passkeys') }}>passkey</a>.</p>
      )}
      <TotpPanel s={s} onChange={reload} />
      <PasskeysPanel s={s} onChange={reload} />
      <TokensPanel />
      {(s.ipRestricted || s.allowlist.length > 0) && (
        <Section id="acct-network" title="Network restriction" subtitle={tr("IP allowlist set by an administrator")}>
          
          <p className="small">{s.ipRestricted ? tr("This account can only be used from these addresses (set by an administrator):") : tr("Addresses on file (restriction currently off):")}</p>
          <ul className="plain small mono">{s.allowlist.map((a) => <li key={a.cidr}>{a.cidr}{a.label ? ` — ${a.label}` : ''}</li>)}</ul>
        </Section>
      )}
      {user?.hasGoogle && !user.hasPassword && <p className="muted small">{tr("You sign in with Google, which handles two-factor authentication for you. These options apply to password sign-in.")}</p>}
    </>
  )
}

function useErr() {
  const [err, setErr] = useState<string | null>(null)
  return { err, setErr, onError: (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Request failed') }
}

function TotpPanel({ s, onChange }: { s: MfaStatus; onChange: () => Promise<void> }) {
  const { err, setErr, onError } = useErr()
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [disablePw, setDisablePw] = useState('')
  const [regenCode, setRegenCode] = useState('')
  const canvas = useRef<HTMLCanvasElement>(null)
  useEffect(() => { if (setup && canvas.current) void QRCode.toCanvas(canvas.current, setup.otpauthUrl, { width: 180, margin: 1 }) }, [setup])

  const start = useMutation({ mutationFn: () => api<{ secret: string; otpauthUrl: string }>('/auth/mfa/totp/setup', { method: 'POST' }), onSuccess: (r) => setSetup(r), onError })
  const enable = useMutation({
    mutationFn: () => api<{ recoveryCodes: string[] }>('/auth/mfa/totp/enable', { method: 'POST', json: { code } }),
    onSuccess: async (r) => { setCodes(r.recoveryCodes); setSetup(null); setCode(''); await onChange() }, onError,
  })
  const disable = useMutation({
    mutationFn: () => api('/auth/mfa/totp/disable', { method: 'POST', json: { password: disablePw } }),
    onSuccess: async () => { setDisablePw(''); setCodes(null); await onChange() }, onError,
  })
  const regen = useMutation({
    mutationFn: () => api<{ recoveryCodes: string[] }>('/auth/mfa/recovery/regenerate', { method: 'POST', json: { code: regenCode } }),
    onSuccess: async (r) => { setCodes(r.recoveryCodes); setRegenCode(''); await onChange() }, onError,
  })

  return (
    <Section id="acct-totp" title="Authenticator app" subtitle={tr("time-based codes (TOTP)")}>
      
      {codes && (
        <div className="notice">
          <p><strong>{tr("Save these recovery codes now.")}</strong> Each works once if you lose your authenticator; they will not be shown again.</p>
          <pre className="codes">{codes.join('\n')}</pre>
          <CopyButton text={codes.join('\n')} tip="Copy all recovery codes" className="link" />{' · '}
          <button className="link" onClick={() => setCodes(null)}>I have saved them</button>
        </div>
      )}
      {!s.totpEnabled && !setup && (
        <>
          <p className="small">{tr("Adds a second step to password sign-in: a 6-digit code from an app such as Google Authenticator, 1Password or Authy.")}</p>
          <div><button className="button primary" onClick={() => { setErr(null); start.mutate() }} disabled={start.isPending}>Set up authenticator</button></div>
        </>
      )}
      {setup && (
        <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); enable.mutate() }}>
          <div className="row">
            <canvas ref={canvas} width={180} height={180} />
            <div className="stack" style={{ flex: 1 }}>
              <p className="small">{tr("1. Scan the code with your authenticator app, or enter this key manually:")}</p>
              <code className="wrap">{setup.secret}</code>
              <p className="small">{tr("2. Enter the 6-digit code the app shows to confirm.")}</p>
              <label>{tr("Code")}<input inputMode="numeric" autoComplete="one-time-code" required minLength={6} maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} /></label>
              <div className="row"><button className="button primary" disabled={enable.isPending}>{tr("Turn on")}</button><button type="button" className="button" onClick={() => setSetup(null)}>Cancel</button></div>
            </div>
          </div>
        </form>
      )}
      {s.totpEnabled && (
        <>
          <p className="good">Enabled. {s.recoveryCodesLeft} recovery code{s.recoveryCodesLeft === 1 ? '' : 's'} left.</p>
          <details>
            <summary className="small">{tr("Regenerate recovery codes")}</summary>
            <form className="row" onSubmit={(e) => { e.preventDefault(); setErr(null); regen.mutate() }}>
              <input placeholder={tr("current 6-digit code")} inputMode="numeric" required minLength={6} maxLength={6} value={regenCode} onChange={(e) => setRegenCode(e.target.value)} />
              <button className="button">{tr("Regenerate")}</button>
            </form>
          </details>
          <details>
            <summary className="small">{tr("Turn off authenticator")}</summary>
            <form className="row" onSubmit={(e) => { e.preventDefault(); setErr(null); disable.mutate() }}>
              <input type="password" placeholder={tr("your password")} autoComplete="current-password" required value={disablePw} onChange={(e) => setDisablePw(e.target.value)} />
              <button className="button danger">{tr("Turn off")}</button>
            </form>
          </details>
        </>
      )}
      {err && <p className="bad">{err}</p>}
    </Section>
  )
}

function PasskeysPanel({ s, onChange }: { s: MfaStatus; onChange: () => Promise<void> }) {
  const { err, setErr, onError } = useErr()
  const supported = typeof window !== 'undefined' && 'PublicKeyCredential' in window
  const add = useMutation({
    mutationFn: async () => {
      const options = await api<Parameters<typeof startRegistration>[0]['optionsJSON']>('/auth/passkeys/register/options', { method: 'POST' })
      const response = await startRegistration({ optionsJSON: options })
      const name = prompt('Name this passkey (e.g. "MacBook Touch ID", "YubiKey")', 'Passkey') || 'Passkey'
      return api('/auth/passkeys/register/verify', { method: 'POST', json: { name, response } })
    },
    onSuccess: onChange,
    onError: (e) => { if (!(e instanceof Error && e.name === 'NotAllowedError')) onError(e) },
  })
  const rename = useMutation({ mutationFn: ({ id, name }: { id: string; name: string }) => api(`/auth/passkeys/${id}`, { method: 'PATCH', json: { name } }), onSuccess: onChange, onError })
  const del = useMutation({ mutationFn: (id: string) => api(`/auth/passkeys/${id}`, { method: 'DELETE' }), onSuccess: onChange, onError })
  return (
    <Section id="acct-passkeys" title="Passkeys" subtitle={tr("fingerprint, face, security key")}>
      
      <p className="small">{tr("Sign in with your device's fingerprint, face or PIN, or a hardware key — no password or code needed. Passkeys are phishing-resistant.")}</p>
      {s.passkeys.length > 0 && (
        <table className="table">
          <thead><tr><th>{tr("Name")}</th><th>{tr("Added")}</th><th>{tr("Last used")}</th><th></th></tr></thead>
          <tbody>
            {s.passkeys.map((p) => (
              <tr key={p.id}>
                <td>{p.name}{p.backedUp && <span className="tag">{tr("synced")}</span>}</td>
                <td className="small">{fmt(p.createdAt)}</td>
                <td className="small">{fmt(p.lastUsedAt)}</td>
                <td>
                  <button className="link" onClick={() => { const n = prompt('Rename passkey', p.name); if (n && n !== p.name) rename.mutate({ id: p.id, name: n }) }}>Rename</button>{' '}
                  <button className="link danger" onClick={() => { if (confirm(`Remove passkey "${p.name}"?`)) del.mutate(p.id) }}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {supported
        ? <div><button className="button" onClick={() => { setErr(null); add.mutate() }} disabled={add.isPending}>Add a passkey</button></div>
        : <p className="muted small">{tr("This browser does not support passkeys.")}</p>}
      {err && <p className="bad">{err}</p>}
    </Section>
  )
}

/** API tokens (§1): the credential for Claude Code's MCP connection and for scripts. Shown once; hashed on the server. */
function TokensPanel() {
  const qc = useQueryClient()
  const { err, setErr, onError } = useErr()
  const q = useQuery({ queryKey: ['api-tokens'], queryFn: () => api<{ tokens: ApiToken[]; mcpUrl: string }>('/auth/tokens') })
  const [name, setName] = useState('')
  const [scope, setScope] = useState<'read' | 'write'>('write')
  const [expires, setExpires] = useState('')
  const [fresh, setFresh] = useState<{ token: string; name: string } | null>(null)
  const create = useMutation({
    mutationFn: () => api<{ token: string; info: ApiToken }>('/auth/tokens', { method: 'POST', json: { name, scope, expiresInDays: expires ? Number(expires) : null } }),
    onSuccess: (r) => { setFresh({ token: r.token, name: r.info.name }); setName(''); void qc.invalidateQueries({ queryKey: ['api-tokens'] }) }, onError,
  })
  const revoke = useMutation({ mutationFn: (id: string) => api(`/auth/tokens/${id}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['api-tokens'] }), onError })
  const mcpUrl = q.data?.mcpUrl ?? ''
  const exportLine = `export APM_TOKEN=${fresh?.token ?? 'apm_…'}`
  const addCmd = `claude mcp add --transport http --scope user apm ${mcpUrl} --header "Authorization: Bearer \${APM_TOKEN}"`
  const live = (q.data?.tokens ?? []).filter((t) => !t.revokedAt)
  const revoked = (q.data?.tokens ?? []).filter((t) => t.revokedAt)
  return (
    <Section id="acct-tokens" title="API tokens" subtitle={tr("for Claude Code and scripts")}>
      <ul className="help small muted">
        <li>{tr("A token lets Claude Code (or a script) act as you through the API: projects, portfolios, tasks, schedules, links, milestones and your time. It never reaches rates, budgets, payroll, approvals, admin or account settings, whatever your role.")}</li>
        <li>{tr("Read tokens can only look; write tokens can also create and change tasks, links, milestones and time entries. Everything a token does is audited as you, marked with the token's name.")}</li>
        <li>{tr("The token is shown once, when you create it. Revoke it here if it leaks.")}</li>
      </ul>
      {fresh && (
        <div className="notice stack" role="status">
          <p><strong>{tr("Token")} "{fresh.name}" {tr("created. Copy it now — it will not be shown again.")}</strong></p>
          <div className="row"><code className="wrap">{fresh.token}</code><CopyButton text={fresh.token} tip="Copy the token" className="link" /></div>
          <p className="small muted">{tr("To use it from Claude Code, put it in your shell and add the server once:")}</p>
          <div className="row"><code className="wrap">{exportLine}</code><CopyButton text={exportLine} tip="Copy the export line" className="link" /></div>
          <div className="row"><code className="wrap">{addCmd}</code><CopyButton text={addCmd} tip="Copy the claude mcp add command" className="link" /></div>
          <p className="small muted">{tr("Then start Claude Code and ask it, for example, \"what is overdue across my portfolio?\" or \"plan the importer epic in DEN with four tasks and link them\".")} <button className="link small" onClick={() => setFresh(null)}>{tr("Dismiss")}</button></p>
        </div>
      )}
      {live.length > 0 && (
        <table className="table">
          <thead><tr><th>{tr("Name")}</th><th>{tr("Scope")}</th><th>{tr("Created")}</th><th>{tr("Last used")}</th><th>{tr("Expires")}</th><th></th></tr></thead>
          <tbody>
            {live.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td><span className={`tag ${t.scope === 'write' ? 'prio-high' : ''}`}>{t.scope}</span></td>
                <td className="small">{fmt(t.createdAt)}</td>
                <td className="small">{fmt(t.lastUsedAt)}</td>
                <td className="small">{t.expiresAt ? fmt(t.expiresAt) : tr("never")}</td>
                <td><button className="link danger" onClick={() => { if (confirm(`Revoke token "${t.name}"? Anything using it stops working immediately.`)) revoke.mutate(t.id) }}>{tr("Revoke")}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {revoked.length > 0 && <p className="small muted">{tr("Revoked:")} {revoked.map((t) => `${t.name} (${fmt(t.revokedAt)})`).join(' · ')}</p>}
      <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); if (name.trim()) create.mutate() }}>
        <input placeholder={tr("Token name, e.g. Claude Code on my laptop")} value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} style={{ flex: 1 }} />
        <select value={scope} onChange={(e) => setScope(e.target.value as 'read' | 'write')} data-tip={tr("Read: look only. Write: create and change tasks, links, milestones and time")}>
          <option value="write">{tr("read and write")}</option>
          <option value="read">{tr("read only")}</option>
        </select>
        <select value={expires} onChange={(e) => setExpires(e.target.value)} data-tip={tr("When the token stops working on its own")}>
          <option value="">{tr("never expires")}</option>
          <option value="30">{tr("30 days")}</option>
          <option value="90">{tr("90 days")}</option>
          <option value="365">{tr("1 year")}</option>
        </select>
        <button className="button primary" disabled={create.isPending}>{tr("Create token")}</button>
      </form>
      {err && <p className="bad">{err}</p>}
    </Section>
  )
}
